import { Text } from "snap-design-system";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import type { ReencryptionProgress as ReencryptionProgressData } from "~/hooks/useReencryption";

interface ReencryptionProgressProps {
    progress: ReencryptionProgressData;
}

/**
 * Progress card showing batch re-encryption status.
 * Follows the same pattern as MigrationProgress.
 */
export function ReencryptionProgress({ progress }: ReencryptionProgressProps) {
    const { total, completed, failed, isRunning } = progress;

    if (total === 0) return null;

    const percentage = total > 0 ? Math.round(((completed + failed) / total) * 100) : 0;
    const isComplete = !isRunning && completed + failed >= total;
    const hasFailures = failed > 0;

    return (
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900">
            <div className="flex items-center gap-3 mb-3">
                {isRunning ? (
                    <Loader2 className="w-5 h-5 animate-spin text-accent-500" />
                ) : isComplete && !hasFailures ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                ) : (
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                )}

                <Text variant="body-md">
                    {isRunning
                        ? `Re-criptografando relatórios (${completed}/${total})...`
                        : isComplete && !hasFailures
                            ? "Re-criptografia concluída com sucesso!"
                            : `Re-criptografia finalizada com ${failed} erro(s).`}
                </Text>
            </div>

            {/* Progress bar */}
            <div className="w-full h-2 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                <div
                    className={`h-full rounded-full transition-all duration-500 ease-out ${hasFailures ? "bg-amber-500" : "bg-accent-500"
                        }`}
                    style={{ width: `${percentage}%` }}
                />
            </div>

            <div className="flex justify-between mt-2">
                <Text variant="body-sm" color="subtle">
                    {completed} concluído(s){failed > 0 ? `, ${failed} falha(s)` : ""}
                </Text>
                <Text variant="body-sm" color="subtle">
                    {percentage}%
                </Text>
            </div>
        </div>
    );
}
